import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity, DocumentType } from "@erp/shared-types";

/**
 * Extract Document Content Step
 *
 * Processes document attachments associated with an inbound email
 * and extracts their content using OCR or document parsing.
 *
 * Filtering behavior (controlled by config):
 * - skipClassificationCheck: true → Process ALL documents regardless of classification
 * - skipClassificationCheck: false (default) → Skip documents classified as OTHER or unclassified
 * - filterForDocumentTypes: When specified, only process documents matching these types
 *
 * This ensures we only run expensive OCR operations on relevant documents.
 *
 * Supports multiple file types:
 * - PDF: ExtendAI or OpenAI Vision OCR
 * - Excel: ExcelJS parsing
 * - DOCX: Office parser
 * - Plaintext: Direct text extraction
 */
const extendSchemaEnum = z.enum(["purchase_order", "ap_invoice"]);

/**
 * Document type filter enum - matches DocumentType from shared-types
 */
const documentTypeEnum = z.enum(DocumentType);

export const extractDocumentContentStep = defineStepConfig({
  type: "extract_document_content",
  name: "Extract Document Content",
  description: "Run OCR and content extraction on document attachments",
  category: "Transformation",

  // TODO
  // Angel: this should probably take:
  // - inboundEmail | document
  // - extraction schema (the caller should fetch/augment this as needed)
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose attachments should be processed",
    ),
    extractionConfig: z
      .object({
        type: z.literal("EXTRACT"),
        schema: z.record(z.string(), z.unknown()),
        advancedOptions: z.record(z.string(), z.unknown()).optional(),
      })
      .describe(
        "Optional extraction schema; defaults to Extend AI schema for the configured type",
      )
      .optional(),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email for downstream steps",
    ),
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe(
        "Array of document entity references (for fanout to per-document processing)",
      ),
    documentOcrResults: z.array(
      z.object({
        document: entityOfType(SystemEntity.CORE_DOCUMENT),
        status: z.enum(["completed", "failed", "skipped"]),
        extractedTextLength: z.number().optional(),
        error: z.string().optional(),
      }),
    ),
    processedCount: z.number(),
    failedCount: z.number(),
    skippedCount: z.number(),
  }),

  configSchema: z.object({
    extendSchemaType: extendSchemaEnum.optional(),
    skipClassificationCheck: z
      .boolean()
      .optional()
      .describe(
        "Skip document classification checks entirely (processes all documents regardless of classification)",
      ),
    filterForDocumentTypes: z
      .array(documentTypeEnum)
      .optional()
      .describe(
        "Only process documents with these classification types. If specified, documents not matching these types are skipped. Overrides the default OTHER-exclusion behavior.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 1,
    },
    nonRetryableErrorTypes: [
      "InvalidFileFormatError",
      "UnsupportedMimeTypeError",
    ],
  },
});
